import {
  type APIThreadMember,
  can,
  PermissionFlags,
} from '@alternativamais/chat-core';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { Avatar } from '../../../componentes/Avatar';
import { avisar, avisarErro } from '../../../componentes/Avisos';
import {
  Botao,
  CaixaMenu,
  Flutuante,
  ItemMenu,
  Modal,
  type Posicao,
  SeparadorMenu,
} from '../../../componentes/Flutuante';
import { Icone } from '../../../componentes/Icone';
import { abrirCamada } from '../../../chat/camadas';
import { useCliente, useEstado } from '../../../chat/contexto';
import { useNome, useModoDesenvolvedor } from '../../../chat/ganchos';
import { Ferramenta } from '../cabecalho/Cabecalho';
import { MenuNotificacoes } from '../cabecalho/MenuNotificacoes';
import { CaixaPopover, Carregando } from '../componentes';
import { Conversa } from '../Conversa';
import { Fixadas } from '../fixadas/Fixadas';
import { FaixaArquivada } from './FaixaArquivada';

/**
 * Thread aberta ao lado do canal (C2): a conversa dela, com o sino, as
 * fixadas, os membros e o menu (entrar, arquivar, trancar, configurar…).
 */
export function PainelThread({
  threadId,
  aoFechar,
  largura = 480,
  textoDoCompositor,
  cabecalhoExtra,
}: {
  threadId: string;
  aoFechar: () => void;
  largura?: number;
  textoDoCompositor?: string;
  /** Post de fórum: etiquetas, contagem e "Seguindo" numa faixa embaixo. */
  cabecalhoExtra?: React.ReactNode;
}) {
  const cliente = useCliente();
  const thread = useEstado((estado) => estado.channels[threadId]);
  const [falhou, setFalhou] = useState(false);

  // Thread que não está na store (arquivada, link antigo): busca.
  useEffect(() => {
    if (thread) return;
    let vivo = true;
    cliente.api
      .channel(threadId)
      .then((canal) => vivo && cliente.store.getState().upsertChannel(canal))
      .catch((erro) => {
        if (!vivo) return;
        setFalhou(true);
        avisarErro(erro);
      });
    return () => {
      vivo = false;
    };
  }, [cliente, thread, threadId]);

  return (
    <aside
      aria-label={thread ? `Thread ${thread.name}` : 'Thread'}
      className="flex shrink-0 flex-col border-l border-divisor bg-chat shadow-[-8px_0_24px_rgba(0,0,0,.28)]"
      style={{ width: largura, maxWidth: '55%' }}
    >
      <header className="flex min-h-12 shrink-0 items-center gap-2 border-b border-divisor py-1.5 pr-2 pl-4">
        <Icone nome="thread" tamanho={20} className="shrink-0 text-texto-3" />
        <div className="flex min-w-0 grow flex-col">
          <h2 className="m-0 truncate text-base font-semibold">
            {thread?.name ?? 'Thread'}
          </h2>
        </div>
        {thread && (
          <div className="flex shrink-0 items-center gap-0.5">
            <Ferramenta rotulo="Notificações da thread" icone="sino">
              {(fechar) => (
                <MenuNotificacoes canalId={threadId} aoFechar={fechar} />
              )}
            </Ferramenta>
            <Ferramenta rotulo="Mensagens fixadas da thread" icone="pin">
              {(fechar) => <Fixadas canalId={threadId} aoFechar={fechar} />}
            </Ferramenta>
            <BotaoMembros threadId={threadId} />
            <MenuThread threadId={threadId} aoFechar={aoFechar} />
            <span className="mx-1 h-6 w-px bg-borda" />
          </div>
        )}
        <button
          type="button"
          aria-label="Fechar thread"
          title="Fechar"
          onClick={aoFechar}
          className="flex size-8 shrink-0 items-center justify-center rounded-md text-texto-3 hover:text-texto"
        >
          <Icone nome="x" tamanho={18} />
        </button>
      </header>
      {cabecalhoExtra && (
        <div className="flex min-h-10 shrink-0 items-center gap-2 border-b border-divisor px-4 py-1.5">
          {cabecalhoExtra}
        </div>
      )}
      {thread ? (
        <div className="flex min-h-0 grow">
          <Conversa
            key={threadId}
            canalId={threadId}
            antesDoCompositor={<FaixaArquivada threadId={threadId} />}
            textoDoCompositor={textoDoCompositor}
          />
        </div>
      ) : (
        <Carregando
          texto={falhou ? 'Não deu para abrir a thread.' : 'Abrindo a thread…'}
        />
      )}
    </aside>
  );
}

function BotaoMembros({ threadId }: { threadId: string }) {
  const total = useEstado(
    (estado) => estado.channels[threadId]?.member_count ?? 0,
  );
  const [ancora, setAncora] = useState<Posicao | null>(null);
  return (
    <>
      <button
        type="button"
        aria-label={`Membros da thread: ${total}`}
        title="Membros da thread"
        onClick={(evento) => {
          const caixa = evento.currentTarget.getBoundingClientRect();
          setAncora(ancora ? null : { x: caixa.right, y: caixa.bottom + 6 });
        }}
        className="flex h-9 items-center gap-1 rounded-lg px-1.5 text-texto-2 hover:text-texto"
      >
        <Icone nome="pessoas" tamanho={20} />
        <span className="text-xs font-semibold">{total}</span>
      </button>
      {ancora && (
        <Flutuante
          ancora={ancora}
          aoFechar={() => setAncora(null)}
          rotulo="Membros da thread"
          alinhar="fim"
        >
          <MembrosThread threadId={threadId} aoFechar={() => setAncora(null)} />
        </Flutuante>
      )}
    </>
  );
}

function MembrosThread({
  threadId,
  aoFechar,
}: {
  threadId: string;
  aoFechar: () => void;
}) {
  const cliente = useCliente();
  const [membros, setMembros] = useState<APIThreadMember[] | null>(null);
  useEffect(() => {
    let vivo = true;
    cliente.api
      .threadMembers(threadId, { with_member: true, limit: 100 })
      .then((lista) => {
        if (!vivo) return;
        cliente.store
          .getState()
          .upsertUsers(
            lista.flatMap((item) =>
              item.member?.user ? [item.member.user] : [],
            ),
          );
        setMembros(lista);
      })
      .catch((erro) => {
        avisarErro(erro);
        if (vivo) setMembros([]);
      });
    return () => {
      vivo = false;
    };
  }, [cliente, threadId]);
  return (
    <CaixaPopover
      titulo="Membros da thread"
      icone="pessoas"
      largura={320}
      altura={420}
      aoFechar={aoFechar}
    >
      {membros === null ? (
        <Carregando />
      ) : (
        <ul className="m-0 flex list-none flex-col p-2">
          {membros.map((membro) => (
            <MembroDaThread
              key={membro.user_id}
              userId={membro.user_id}
              threadId={threadId}
            />
          ))}
        </ul>
      )}
    </CaixaPopover>
  );
}

function MembroDaThread({
  userId,
  threadId,
}: {
  userId: string;
  threadId: string;
}) {
  const guildId = useEstado(
    (estado) => estado.channels[threadId]?.guild_id ?? null,
  );
  const usuario = useEstado((estado) => estado.users[userId]);
  const nome = useNome(userId, guildId);
  const status = useEstado(
    (estado) => estado.presences[userId]?.status ?? 'offline',
  );
  if (!usuario) return null;
  return (
    <li className="flex h-10 items-center gap-2.5 rounded-md px-2 hover:bg-hover">
      <Avatar
        pessoa={usuario}
        nome={nome}
        tamanho={28}
        status={status}
        fundo="var(--cor-elevado)"
      />
      <span className="truncate text-[15px] font-medium">{nome}</span>
    </li>
  );
}

/** Menu da thread: entrar/sair, arquivar, trancar, configurar, copiar, excluir. */
function MenuThread({
  threadId,
  aoFechar,
}: {
  threadId: string;
  aoFechar: () => void;
}) {
  const dev = useModoDesenvolvedor();
  const cliente = useCliente();
  const navegar = useNavigate();
  const thread = useEstado((estado) => estado.channels[threadId]);
  const participa = useEstado((estado) => !!estado.joinedThreads[threadId]);
  const euId = useEstado((estado) => estado.me?.id);
  const gerencia = useEstado((estado) =>
    can(estado, threadId, PermissionFlags.MANAGE_THREADS),
  );
  const [ancora, setAncora] = useState<Posicao | null>(null);
  const [excluindo, setExcluindo] = useState(false);
  if (!thread) return null;
  const dono = thread.owner_id === euId;
  const arquivada = !!thread.thread_metadata?.archived;
  const trancada = !!thread.thread_metadata?.locked;
  const fechar = () => setAncora(null);
  const mudar = (corpo: Parameters<typeof cliente.api.modifyChannel>[1]) =>
    cliente.api
      .modifyChannel(threadId, corpo)
      .then((canal) => cliente.store.getState().upsertChannel(canal))
      .catch(avisarErro);
  const link = `${window.location.origin}/canais/${thread.guild_id}/${threadId}`;
  return (
    <>
      <button
        type="button"
        aria-label="Mais opções da thread"
        title="Mais opções"
        aria-haspopup="menu"
        aria-expanded={!!ancora}
        onClick={(evento) => {
          const caixa = evento.currentTarget.getBoundingClientRect();
          setAncora(ancora ? null : { x: caixa.right, y: caixa.bottom + 6 });
        }}
        className="flex size-9 items-center justify-center rounded-lg text-texto-2 hover:text-texto"
      >
        <Icone nome="mais-opcoes" tamanho={20} />
      </button>
      {ancora && (
        <Flutuante
          ancora={ancora}
          aoFechar={fechar}
          rotulo="Opções da thread"
          papel="menu"
          alinhar="fim"
        >
          <CaixaMenu largura={240}>
            <ItemMenu
              icone={participa ? 'sair' : 'usuario-mais'}
              aoEscolher={() => {
                fechar();
                (participa
                  ? cliente.api.leaveThread(threadId)
                  : cliente.api.joinThread(threadId)
                ).catch(avisarErro);
              }}
            >
              {participa ? 'Sair da thread' : 'Entrar na thread'}
            </ItemMenu>
            <ItemMenu
              icone="abrir-fora"
              aoEscolher={() => {
                fechar();
                navegar(`/canais/${thread.guild_id}/${threadId}`);
              }}
            >
              Abrir em tela cheia
            </ItemMenu>
            {(gerencia || dono) && (
              <>
                <SeparadorMenu />
                <ItemMenu
                  aoEscolher={() => {
                    fechar();
                    void mudar({ archived: !arquivada });
                  }}
                >
                  {arquivada ? 'Desarquivar thread' : 'Arquivar thread'}
                </ItemMenu>
                {gerencia && (
                  <ItemMenu
                    icone="cadeado"
                    aoEscolher={() => {
                      fechar();
                      void mudar({ locked: !trancada });
                    }}
                  >
                    {trancada ? 'Destrancar thread' : 'Trancar thread'}
                  </ItemMenu>
                )}
                <ItemMenu
                  icone="engrenagem"
                  aoEscolher={() => {
                    fechar();
                    abrirCamada({ tipo: 'config-thread', threadId });
                  }}
                >
                  Configurações da thread
                </ItemMenu>
              </>
            )}
            <SeparadorMenu />
            <ItemMenu
              icone="link"
              aoEscolher={() => {
                fechar();
                void navigator.clipboard
                  ?.writeText(link)
                  .then(() => avisar('Link copiado.', 'info'));
              }}
            >
              Copiar link
            </ItemMenu>
            {dev && (
              <ItemMenu
                icone="copiar"
                aoEscolher={() => {
                  fechar();
                  void navigator.clipboard
                    ?.writeText(threadId)
                    .then(() => avisar('ID copiado.', 'info'));
                }}
              >
                Copiar ID da thread
              </ItemMenu>
            )}
            {gerencia && (
              <>
                <SeparadorMenu />
                <ItemMenu
                  perigo
                  icone="lixeira"
                  aoEscolher={() => {
                    fechar();
                    setExcluindo(true);
                  }}
                >
                  Excluir thread
                </ItemMenu>
              </>
            )}
          </CaixaMenu>
        </Flutuante>
      )}
      {excluindo && (
        <Modal
          titulo="Excluir thread"
          descricao={`A thread "${thread.name}" e todas as mensagens dela vão sumir. Não dá para desfazer.`}
          aoFechar={() => setExcluindo(false)}
          rodape={
            <>
              <Botao tipo="fantasma" onClick={() => setExcluindo(false)}>
                Cancelar
              </Botao>
              <Botao
                tipo="perigo"
                data-foco-inicial
                onClick={() => {
                  setExcluindo(false);
                  cliente.api
                    .deleteChannel(threadId)
                    .then(aoFechar)
                    .catch(avisarErro);
                }}
              >
                Excluir
              </Botao>
            </>
          }
        />
      )}
    </>
  );
}
